/**
 * Correcting a registered patient's demographics (DECISIONS.md #43).
 *
 * Mirrors `api/src/Continuum.Api/Patients/PatientEndpoints.cs`'s
 * `POST /api/patients/{patientId}/versions` and the codes `PatientCorrection.Validate` and
 * `PatientCorrectionService` return.
 *
 * **`/versions` and a POST, not a PUT onto the patient.** There is no resource to replace: this
 * appends the next version and the one on screen stays in the record (AGENTS.md §3 rule 2). A PUT
 * would name the act after what it is not.
 *
 * **The personnummer is not here, and that is not an omission.** #40 refuses a typed personnummer
 * client-side because phase 0 must never hold a real one (AGENTS.md §6), and correcting a patient
 * *into* holding one would be the same write by another route. Five demographic fields, and the
 * identifier the record was filed under is untouched — which is also what keeps the wristband, the
 * labels and yesterday's notes still pointing at this patient.
 *
 * **Not idempotent, and not retryable on a timeout**, for the reason `registerPatient` gives. What
 * makes that survivable is {@link CorrectionSubmission.supersedes}: a retry after an uncertain
 * outcome names a version that is no longer current and is refused with
 * `CORRECTION_VERSION_STALE` rather than writing version 3 that repeats version 2.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * What the form sends. Mirrors `PatientCorrectionRequest`.
 *
 * Every field is sent on every correction, including the ones the clinician did not touch: the
 * body is the demographics as they should now read, not a list of edits. A field left out would
 * be indistinguishable from a field cleared on purpose, and clearing a birth date that was a
 * guess is a correction somebody will want to make.
 */
export interface CorrectionSubmission {
  /**
   * The `version_id` this correction replaces — the one the screen was showing.
   *
   * **Required, and the reason the overview carries `versionId` at all.** Inferring "whatever is
   * current" would let two clinicians correcting one unidentified patient both succeed, with the
   * second silently overwriting the first's reading of the same wristband.
   */
  readonly supersedes: string

  readonly givenName: string | null
  readonly familyName: string | null

  /** ISO `YYYY-MM-DD`, or null when the birth date is not known. */
  readonly birthDate: string | null

  /** HL7 administrative gender as a code, or null. `unknown` is a real answer, not an absence. */
  readonly sex: string | null

  /**
   * Whether the identity is still a working assumption.
   *
   * **An explicit value on every correction, never inferred from whether the name boxes are
   * full.** #43's open clinical question — whether a patient telling you their name is enough to
   * clear this, or whether it takes an ID document — is the clinician's to answer, and it is
   * answered per patient by this control. A form that cleared the flag because a name had been
   * typed would answer it for them, permanently and in the record.
   */
  readonly isEstimatedIdentity: boolean
}

/** What a successful correction returns. Mirrors `CorrectedPatient`. */
export interface CorrectedPatient {
  readonly patientId: string

  /** The new version's `version_id`. What the *next* correction must supersede. */
  readonly versionId: string

  /** 2 for the first correction, then 3, 4 … */
  readonly version: number

  /** The `version_id` that was superseded, echoed back from the row that was written. */
  readonly supersedes: string
}

/**
 * Every code a correction can be refused with.
 *
 * Listed rather than left as `string` so that a code added on the server without wording here is
 * a typecheck failure rather than a blank message on a screen where a clinician has just
 * established who a patient is. `correctionRefusalMessage` is exhaustive over this by
 * construction.
 *
 * Three are not `CORRECTION_` codes. `PATIENT_NOT_FOUND` and `PATIENT_RETRACTED` are facts about
 * the record rather than about the form, and `ROLE_MAY_NOT_DOCUMENT` comes from `AccessGate`,
 * shared with the note write and the registration.
 */
export const CORRECTION_REFUSAL_CODES = [
  'CORRECTION_BODY_MISSING',
  'CORRECTION_SUPERSEDES_MISSING',
  'CORRECTION_FAMILY_NAME_MISSING',
  'CORRECTION_FIELD_TOO_LONG',
  'CORRECTION_SEX_UNKNOWN',
  'CORRECTION_BIRTH_DATE_MALFORMED',
  'CORRECTION_BIRTH_DATE_IN_FUTURE',
  'CORRECTION_VERSION_STALE',
  'CORRECTION_NO_CHANGE',
  'PATIENT_NOT_FOUND',
  'PATIENT_RETRACTED',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type CorrectionRefusalCode = (typeof CORRECTION_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface CorrectionRefusalParams {
  /** `CORRECTION_FIELD_TOO_LONG`: which field, by its submission name, and the limit. */
  readonly field?: string
  readonly max?: number

  /** `CORRECTION_SEX_UNKNOWN`: what was sent. */
  readonly sex?: string

  /** The two birth-date codes: what was typed, quoted back. */
  readonly birthDate?: string

  /** `CORRECTION_VERSION_STALE`: the version that was named. */
  readonly supersedes?: string

  /**
   * `CORRECTION_VERSION_STALE`: the version that is current instead — and **absent when the race
   * was lost to a concurrent insert** rather than to a version that had already moved.
   *
   * Not displayed either way. The screen's answer to staleness is to refetch, which reads the
   * current version from the record rather than trusting a value that could have moved again
   * between the refusal and the render.
   */
  readonly current?: string

  /** `PATIENT_NOT_FOUND`: the id that did not resolve. */
  readonly patientId?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/**
 * The server refused the correction, with the code it refused by.
 *
 * Distinct from {@link CorrectionUnavailableError} for the reason `RegistrationRefusedError`
 * gives: a refusal is something to fix on the form, and an unavailable API is something to wait
 * out **without closing the form**.
 */
export class CorrectionRefusedError extends Error {
  readonly code: CorrectionRefusalCode | 'UNKNOWN'
  readonly params: CorrectionRefusalParams
  readonly status: number

  constructor(
    code: CorrectionRefusalCode | 'UNKNOWN',
    params: CorrectionRefusalParams,
    status: number,
  ) {
    super(`Correction refused: ${code}`)
    this.name = 'CorrectionRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class CorrectionUnavailableError extends Error {
  constructor() {
    super('Correction unavailable')
    this.name = 'CorrectionUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is CorrectionRefusalCode {
  return (
    typeof value === 'string' && (CORRECTION_REFUSAL_CODES as readonly string[]).includes(value)
  )
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable body still becomes a {@link CorrectionRefusedError} with code `UNKNOWN` rather
 * than an unavailable error, because the server did answer: nothing was written, and telling the
 * clinician to wait would be wrong.
 */
async function refusalFrom(response: Response): Promise<CorrectionRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: CorrectionRefusalParams }
    }

    return new CorrectionRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new CorrectionRefusedError('UNKNOWN', {}, response.status)
  }
}

/**
 * Corrects a patient's demographics: one new `clinical.patient` version and its audit row, in one
 * transaction on the server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). Version 1 stays readable — that is the
 * point of the design, not a limitation of it — so a correction that was itself wrong is fixed by
 * a further correction, and the history shows both.
 */
export async function correctPatient(
  patientId: string,
  submission: CorrectionSubmission,
  signal: AbortSignal,
): Promise<CorrectedPatient> {
  let response: Response

  try {
    response = await fetch(`/api/patients/${encodeURIComponent(patientId)}/versions`, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request. Re-thrown so the caller can
    // tell it from a failure — but a write that reached the server still happened, which is why
    // the form does not abort a submit in flight. See `PatientCorrectionScreen`.
    if (signal.aborted) {
      throw cause
    }
    throw new CorrectionUnavailableError()
  }

  // Before `refusalFrom`, which reads the body, and before it for the reason as much as the
  // order: a 401 is the session having ended rather than a refusal of this correction.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as CorrectedPatient
  } catch {
    // A 201 whose body did not parse: the correction is in the record and this client cannot say
    // which version it became. Reported as unavailable so the success path is not taken — the
    // clinician reopens the patient and reads what the record now says, rather than being told
    // the correction failed and writing a third version that repeats the second.
    throw new CorrectionUnavailableError()
  }
}
